/**
 * Enhanced authentication utilities with security features
 */

// Token storage keys
const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const TOKEN_EXPIRY_KEY = "tokenExpiry";
const SESSION_EXPIRY_KEY = "sessionExpiry";
const REMEMBER_ME_KEY = "rememberMe";

const SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getStorage() {
  return localStorage.getItem(REMEMBER_ME_KEY) === "true"
    ? localStorage
    : sessionStorage;
}

/**
 * Secure token storage with expiration tracking
 */
export class TokenManager {
  static setTokens(accessToken, refreshToken, expiresIn = 900, rememberMe = null) {
    try {
      // Persist the remember-me preference first so getStorage() works correctly
      if (rememberMe !== null) {
        localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? "true" : "false");
      }

      const storage = getStorage();
      const accessExpiry = Date.now() + expiresIn * 1000;

      const existingSessionExpiry = storage.getItem(SESSION_EXPIRY_KEY);
      const sessionExpiry = existingSessionExpiry
        ? parseInt(existingSessionExpiry)
        : Date.now() + SESSION_DURATION_MS;

      storage.setItem(ACCESS_TOKEN_KEY, accessToken);
      storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      storage.setItem(TOKEN_EXPIRY_KEY, accessExpiry.toString());
      storage.setItem(SESSION_EXPIRY_KEY, sessionExpiry.toString());

      window.dispatchEvent(
        new CustomEvent("tokenUpdated", {
          detail: { accessToken, refreshToken, accessExpiry, sessionExpiry },
        })
      );
    } catch (error) {
      console.error("Failed to store tokens:", error);
      throw new Error("Failed to store authentication tokens");
    }
  }

  static getAccessToken() {
    try {
      const storage = getStorage();
      const token = storage.getItem(ACCESS_TOKEN_KEY);
      if (!token) return null;

      const sessionExpiry = storage.getItem(SESSION_EXPIRY_KEY);
      if (sessionExpiry && Date.now() >= parseInt(sessionExpiry)) {
        this.clearTokens();
        return null;
      }

      return token;
    } catch (error) {
      console.error("Failed to retrieve access token:", error);
      return null;
    }
  }

  static getRefreshToken() {
    try {
      return getStorage().getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to retrieve refresh token:", error);
      return null;
    }
  }

  static clearTokens() {
    try {
      [localStorage, sessionStorage].forEach((s) => {
        s.removeItem(ACCESS_TOKEN_KEY);
        s.removeItem(REFRESH_TOKEN_KEY);
        s.removeItem(TOKEN_EXPIRY_KEY);
        s.removeItem(SESSION_EXPIRY_KEY);
      });
      localStorage.removeItem(REMEMBER_ME_KEY);

      window.dispatchEvent(new CustomEvent("tokenCleared"));
    } catch (error) {
      console.error("Failed to clear tokens:", error);
    }
  }

  static isTokenExpired() {
    const expiry = getStorage().getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return true;
    return Date.now() >= parseInt(expiry);
  }

  static getTokenExpiry() {
    const expiry = getStorage().getItem(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry) : null;
  }

  static getSessionExpiry() {
    const expiry = getStorage().getItem(SESSION_EXPIRY_KEY);
    return expiry ? parseInt(expiry) : null;
  }

  static isAuthenticated() {
    const storage = getStorage();
    const token = storage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return false;
    const sessionExpiry = storage.getItem(SESSION_EXPIRY_KEY);
    if (sessionExpiry && Date.now() >= parseInt(sessionExpiry)) {
      this.clearTokens();
      return false;
    }
    return true;
  }
}

/**
 * Session management utilities
 */
export class SessionManager {
  static SESSION_WARNING_TIME = 30 * 60 * 1000; // warn 30 min before session end
  static ACCESS_TOKEN_REFRESH_BUFFER = 2 * 60 * 1000; // refresh 2 min before expiry
  static sessionWarningShown = false;
  static refreshTimer = null;
  static warningTimer = null;
  static proactiveRefreshTimer = null;
  static onRefreshCallback = null;

  static startSessionMonitoring(
    onSessionExpiring,
    onSessionExpired,
    onProactiveRefresh = null
  ) {
    this.clearTimers();
    this.onRefreshCallback = onProactiveRefresh;

    const checkSession = () => {
      const sessionExpiry = TokenManager.getSessionExpiry();
      if (!sessionExpiry) return;

      const timeUntilSessionEnd = sessionExpiry - Date.now();

      if (timeUntilSessionEnd <= 0) {
        this.clearTimers();
        onSessionExpired?.();
        return;
      }

      if (
        timeUntilSessionEnd <= this.SESSION_WARNING_TIME &&
        !this.sessionWarningShown
      ) {
        this.sessionWarningShown = true;
        onSessionExpiring?.(Math.floor(timeUntilSessionEnd / 1000));

        this.refreshTimer = setTimeout(() => {
          onSessionExpired?.();
        }, timeUntilSessionEnd);
      }
    };

    const scheduleProactiveRefresh = () => {
      if (this.proactiveRefreshTimer) {
        clearTimeout(this.proactiveRefreshTimer);
        this.proactiveRefreshTimer = null;
      }

      const accessExpiry = TokenManager.getTokenExpiry();
      if (!accessExpiry) return;

      const timeUntilAccessExpiry = accessExpiry - Date.now();
      const refreshIn = Math.max(
        timeUntilAccessExpiry - this.ACCESS_TOKEN_REFRESH_BUFFER,
        0
      );

      this.proactiveRefreshTimer = setTimeout(async () => {
        if (!TokenManager.isAuthenticated()) return;
        try {
          if (this.onRefreshCallback) {
            await this.onRefreshCallback();
          }
        } catch {
          // Reactive refresh via axios interceptor will handle it
        }
        scheduleProactiveRefresh();
      }, refreshIn);
    };

    checkSession();
    scheduleProactiveRefresh();

    this.warningTimer = setInterval(checkSession, 60000);
  }

  static clearTimers() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.warningTimer) {
      clearInterval(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
    this.sessionWarningShown = false;
    this.onRefreshCallback = null;
  }

  static extendSession() {
    this.sessionWarningShown = false;
    this.clearTimers();
  }
}

/**
 * Permission checking utilities
 */
export class PermissionManager {
  static PERMISSIONS = {
    VIEW: "view",
    EDIT: "edit",
    // Note: Backend only supports 'view' and 'edit' permission levels
    // 'edit' in backend corresponds to full access (view, add, edit, delete)
  };

  static hasPermission(userPermissions, requiredPermission) {
    if (!userPermissions || !requiredPermission) return false;

    const permissionHierarchy = {
      [this.PERMISSIONS.VIEW]: 1,
      [this.PERMISSIONS.EDIT]: 2,
    };

    const userLevel = permissionHierarchy[userPermissions] || 0;
    const requiredLevel = permissionHierarchy[requiredPermission] || 0;

    return userLevel >= requiredLevel;
  }

  static canViewChild(userPermissions) {
    return this.hasPermission(userPermissions, this.PERMISSIONS.VIEW);
  }

  static canEditChild(userPermissions) {
    return this.hasPermission(userPermissions, this.PERMISSIONS.EDIT);
  }

  static canDeleteChild(userPermissions) {
    return this.hasPermission(userPermissions, this.PERMISSIONS.EDIT);
  }

  static canManageSharing(_userPermissions, _user = null) {
    // Users can manage sharing for their own children if they are authenticated
    // The actual permission check happens on the backend based on child ownership
    return true; // All authenticated users can attempt to manage sharing
  }
}

/**
 * Input sanitization utilities
 */
export class SecurityUtils {
  /**
   * Sanitize HTML input to prevent XSS
   */
  static sanitizeHtml(input) {
    if (typeof input !== "string") return input;

    const div = document.createElement("div");
    div.textContent = input;
    return div.innerHTML;
  }

  /**
   * Sanitize user input for API calls
   */
  static sanitizeInput(input) {
    if (typeof input !== "string") return input;

    return input
      .replace(/[<>]/g, "")
      .substring(0, 1000);
  }

  /**
   * Validate email format
   */
  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  static validatePassword(password) {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];

    if (password.length < minLength) {
      errors.push(`Password must be at least ${minLength} characters long`);
    }

    if (!hasUpperCase) {
      errors.push("Password must contain at least one uppercase letter");
    }

    if (!hasLowerCase) {
      errors.push("Password must contain at least one lowercase letter");
    }

    if (!hasNumbers) {
      errors.push("Password must contain at least one number");
    }

    if (!hasSpecialChar) {
      errors.push("Password must contain at least one special character");
    }

    return {
      isValid: errors.length === 0,
      errors,
      strength: this.getPasswordStrength(password),
    };
  }

  /**
   * Get password strength score
   */
  static getPasswordStrength(password) {
    let score = 0;

    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    if (score < 3) return "weak";
    if (score < 5) return "medium";
    return "strong";
  }

  /**
   * Generate secure random string
   */
  static generateSecureId(length = 16) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";

    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }

  /**
   * Mask sensitive data for logging
   */
  static maskSensitiveData(
    data,
    sensitiveFields = ["password", "token", "email"]
  ) {
    if (typeof data !== "object" || data === null) return data;

    const masked = { ...data };

    sensitiveFields.forEach((field) => {
      if (masked[field]) {
        if (field === "email" && typeof masked[field] === "string") {
          const email = masked[field];
          const [local, domain] = email.split("@");
          masked[field] = `${local[0]}***@${domain[0]}*****.${domain
            .split(".")
            .pop()}`;
        } else {
          // Mask other sensitive fields
          masked[field] = "***";
        }
      }
    });

    return masked;
  }
}

/**
 * Route protection utilities
 */
export class RouteGuard {
  static PROTECTED_ROUTES = ["/dashboard", "/profile", "/sharing", "/children"];

  static PUBLIC_ROUTES = ["/", "/login", "/register", "/password-reset"];

  static isProtectedRoute(pathname) {
    return this.PROTECTED_ROUTES.some((route) => pathname.startsWith(route));
  }

  static isPublicRoute(pathname) {
    return this.PUBLIC_ROUTES.some(
      (route) => pathname === route || (route === "/" && pathname === "/")
    );
  }

  static shouldRedirectToLogin(pathname, isAuthenticated) {
    return this.isProtectedRoute(pathname) && !isAuthenticated;
  }

  static shouldRedirectToDashboard(pathname, isAuthenticated) {
    const authRoutes = ["/login", "/register"];
    return authRoutes.includes(pathname) && isAuthenticated;
  }

  static getRedirectPath(from) {
    // Store the attempted path for redirect after login
    if (this.isProtectedRoute(from)) {
      sessionStorage.setItem("redirectAfterLogin", from);
      return "/login";
    }

    return "/dashboard";
  }

  static getPostLoginRedirect() {
    const stored = sessionStorage.getItem("redirectAfterLogin");
    sessionStorage.removeItem("redirectAfterLogin");
    return stored || "/dashboard";
  }
}

/**
 * Enhanced API client with authentication and security
 */
export class SecureApiClient {
  constructor(baseURL = "") {
    this.baseURL = baseURL;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
  }

  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
  }

  async request(url, options = {}) {
    let config = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      ...options,
    };

    // Add authentication header
    const token = TokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      config = await interceptor(config);
    }

    try {
      const response = await fetch(`${this.baseURL}${url}`, config);

      // Apply response interceptors
      let processedResponse = response;
      for (const interceptor of this.responseInterceptors) {
        processedResponse = await interceptor(processedResponse);
      }

      return processedResponse;
    } catch (error) {
      // Handle network errors
      throw new Error("Network error. Please check your connection.");
    }
  }

  async get(url, options = {}) {
    return this.request(url, { ...options, method: "GET" });
  }

  async post(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async put(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async patch(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async delete(url, options = {}) {
    return this.request(url, { ...options, method: "DELETE" });
  }
}

// Export default instances
export const tokenManager = TokenManager;
export const sessionManager = SessionManager;
export const permissionManager = PermissionManager;
export const securityUtils = SecurityUtils;
export const routeGuard = RouteGuard;

// Create default secure API client
export const secureApiClient = new SecureApiClient(
  import.meta.env.VITE_API_URL || "http://localhost:8000"
);
